import { useState, useEffect } from "react";
import DataTable from "../../components/DataTable";
import Swal from "sweetalert2";
import { api } from "../../axios/api";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { Plus, Edit, Trash2 } from "lucide-react";

const UsersTab = () => {
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState({});
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  
  const userSession = useSelector((state) => state.user.user);
  const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");

  const texts = {
    "pt-AO": {
      title: "Lista de Acessos",
      btnAdd: "Adicionar",
      btnEdit: "Editar",
      btnDelete: "Eliminar",
      colId: "ID",
      colName: "NOME COMPLETO",
      colPhone: "TELEFONE",
      colEmail: "E-MAIL",
      colLevel: "NÍVEL",
      modalNew: "NOVO UTILIZADOR",
      modalEdit: "EDITAR UTILIZADOR",
      labelPhoto: "FOTO DE PERFIL",
      labelName: "NOME COMPLETO",
      labelPhone: "TELEFONE",
      labelLevel: "NÍVEL DE ACESSO",
      labelEmail: "E-MAIL",
      labelPass: "PASSWORD",
      passHint: "Mínimo 6 caracteres",
      btnSave: "Gravar Dados",
      btnClose: "Fechar",
      swalInfo: "Selecione um utilizador na tabela primeiro.",
      swalConfirm: "Deseja realmente eliminar este utilizador?",
      swalSuccess: "Dados guardados com sucesso.",
      levels: ["Administrador", "Caixa", "Secretaria"]
    },
    "en-US": {
      title: "Access List",
      btnAdd: "Add New",
      btnEdit: "Edit",
      btnDelete: "Delete",
      colId: "ID",
      colName: "FULL NAME",
      colPhone: "PHONE",
      colEmail: "E-MAIL",
      colLevel: "LEVEL",
      modalNew: "NEW USER",
      modalEdit: "EDIT USER",
      labelPhoto: "PROFILE PHOTO",
      labelName: "FULL NAME",
      labelPhone: "PHONE NUMBER",
      labelLevel: "ACCESS LEVEL",
      labelEmail: "E-MAIL",
      labelPass: "PASSWORD",
      passHint: "Minimum 6 characters",
      btnSave: "Save Data",
      btnClose: "Close",
      swalInfo: "Please select a user from the table first.",
      swalConfirm: "Are you sure you want to delete this user?",
      swalSuccess: "Data saved successfully.",
      levels: ["Administrator", "Cashier", "Secretary"]
    },
    "zh-CN": {
      title: "访问列表",
      btnAdd: "添加",
      btnEdit: "编辑",
      btnDelete: "删除",
      colId: "ID",
      colName: "全名",
      colPhone: "电话",
      colEmail: "电子邮件",
      colLevel: "级别",
      modalNew: "新用户",
      modalEdit: "编辑用户",
      labelPhoto: "个人资料照片",
      labelName: "全名",
      labelPhone: "电话号码",
      labelLevel: "访问级别",
      labelEmail: "电子邮件",
      labelPass: "密码",
      passHint: "最少 6 个字符",
      btnSave: "保存数据",
      btnClose: "关闭",
      swalInfo: "请先从表格中选择一个用户。",
      swalConfirm: "您确定要删除此用户吗？",
      swalSuccess: "数据保存成功。",
      levels: ["管理员", "收银员", "秘书"]
    },
    "fr-FR": {
      title: "Liste des Accès",
      btnAdd: "Ajouter",
      btnEdit: "Modifier",
      btnDelete: "Supprimer",
      colId: "ID",
      colName: "NOM COMPLET",
      colPhone: "TÉLÉPHONE",
      colEmail: "E-MAIL",
      colLevel: "NIVEAU",
      modalNew: "NOUVEL UTILISATEUR",
      modalEdit: "MODIFIER UTILISATEUR",
      labelPhoto: "PHOTO DE PROFIL",
      labelName: "NOM COMPLET",
      labelPhone: "TÉLÉPHONE",
      labelLevel: "NIVEAU D'ACCÈS",
      labelEmail: "E-MAIL",
      labelPass: "MOT DE PASSE",
      passHint: "Minimum 6 caractères",
      btnSave: "Enregistrer",
      btnClose: "Fermer",
      swalInfo: "Veuillez d'abord sélectionner un utilisateur dans le tableau.",
      swalConfirm: "Voulez-vous vraiment supprimer cet utilisateur ?",
      swalSuccess: "Données enregistrées avec succès.",
      levels: ["Administrateur", "Caissier", "Secrétariat"]
    },

    "ar-SA": {
      title: "قائمة المستخدمين",
      btnAdd: "إضافة",
      btnEdit: "تعديل",
      btnDelete: "حذف",
      colId: "ID",
      colName: "الاسم الكامل",
      colPhone: "الهاتف",
      colEmail: "البريد الإلكتروني",
      colLevel: "المستوى",
      modalNew: "مستخدم جديد",
      modalEdit: "تعديل المستخدم",
      labelPhoto: "صورة الملف الشخصي",
      labelName: "الاسم الكامل",
      labelPhone: "رقم الهاتف",
      labelLevel: "مستوى الوصول",
      labelEmail: "البريد الإلكتروني",
      labelPass: "كلمة المرور",
      passHint: "الحد الأدنى 6 أحرف",
      btnSave: "حفظ البيانات",
      btnClose: "إغلاق",
      swalInfo: "يرجى اختيار مستخدم من الجدول أولاً.",
      swalConfirm: "هل أنت متأكد من حذف هذا المستخدم؟",
      swalSuccess: "تم حفظ البيانات بنجاح.",
      levels: ["مدير", "أمين صندوق", "سكرتارية"]
    }
  };

  const t = texts[language] || texts["pt-AO"];

  const fetchUsers = async () => {
    try {
      const response = await api.get("/auth/users/empresa/" + userSession.empresa_id);
      setData(response.data);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSelectRow = (e) => {
    const tr = e.target.closest("tr");
    if (tr && tr.parentElement.tagName === "TBODY") {
      const firstCell = tr.cells[0];
      if (firstCell) {
        const id = parseInt(firstCell.innerText);
        if (!isNaN(id)) {
          setSelectedId(id);
          const allRows = tr.parentElement.querySelectorAll("tr");
          allRows.forEach(row => row.classList.remove("win10-row-selected"));
          tr.classList.add("win10-row-selected");
        }
      }
    }
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setUserData({ nivel: "Caixa" });
    setShowModal(true);
  };

  const handleOpenEditModal = () => {
    if (!selectedId) {
      return Swal.fire({ title: "Info", text: t.swalInfo, icon: "info", confirmButtonColor: "#0078d4" });
    }
    const user = data.find((u) => u.id === selectedId);
    if (user) {
      setIsEditing(true);
      setUserData(user);
      setShowModal(true);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    Swal.fire({
      title: "Confirm",
      text: t.swalConfirm,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d13438",
      cancelButtonColor: "#8a8886"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/auth/users/${selectedId}`);
          setData(data.filter((u) => u.id !== selectedId));
          setSelectedId(null);
        } catch (error) {
          Swal.fire("Error", "Action failed", "error");
        }
      }
    });
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      Object.keys(userData).forEach(key => {
        if (key === 'foto' && userData[key] instanceof File) {
          formData.append("file", userData[key]);
        } else {
          formData.append(key, userData[key] || "");
        }
      });
      formData.append("empresa_id", userSession.empresa_id);

      if (isEditing) {
        await api.put(`/auth/users/${userData.id}`, { ...userData, empresa_id: userSession.empresa_id });
      } else {
        await api.post("/auth/users", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      
      fetchUsers();
      setShowModal(false);
      setSelectedId(null);
      Swal.fire("OK", t.swalSuccess, "success");
    } catch (e) {
      Swal.fire("Error", "Fail", "error");
    }
  };

  const columns = [
    { title: t.colId, data: "id" },
    { title: t.colName, data: "nome" },
    { title: t.colPhone, data: "telefone" },
    { title: t.colEmail, data: "email" },
    { title: t.colLevel, data: "nivel" },
  ];

  return (
    <>
      <style>{`
        .win10-card { background: #fff; border: 1px solid #e1e1e1; border-radius: 0px; }
        .win10-header { border-bottom: 1px solid #eee; padding: 15px 20px; background: #fff; }
        .win10-row-selected { background-color: #0078d4 !important; color: #fff !important; }
        .win10-btn { border-radius: 0px; font-size: 13px; padding: 6px 16px; border: 1px solid transparent; }
        .win10-btn-primary { background: #0078d4; color: #fff; }
        .win10-btn-outline { background: #fff; border: 1px solid #8a8886; color: #323130; }
        .win10-input { border: 1px solid #8a8886; border-radius: 0; font-size: 14px; }
        .win10-input:focus { border-color: #0078d4; outline: 1px solid #0078d4; box-shadow: none; }
      `}</style>

      <div className="animate__animated animate__fadeIn">
        <div className="win10-card shadow-sm">
          <div className="win10-header d-flex justify-content-between align-items-center">
            <span className="text-muted small fw-bold text-uppercase">{t.title}</span>
            <div className="d-flex gap-1">
              <button className="win10-btn win10-btn-primary d-flex align-items-center gap-2" onClick={handleOpenAddModal}>
                <Plus size={14} /> {t.btnAdd}
              </button>
              <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={handleOpenEditModal}>
                <Edit size={14} /> {t.btnEdit}
              </button>
              <button className="win10-btn win10-btn-outline text-danger d-flex align-items-center gap-2" onClick={handleDelete}>
                <Trash2 size={14} /> {t.btnDelete}
              </button>
            </div>
          </div>
          
          <div className="card-body p-0" onClick={handleSelectRow}>
            <div className="p-3 overflow-auto">
              <DataTable columns={columns} data={data} className="win10-table" />
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" contentClassName="rounded-0 border-dark">
        <Modal.Header closeButton className="border-bottom pb-2">
          <Modal.Title className="fs-6 fw-bold text-uppercase">
            {isEditing ? t.modalEdit : t.modalNew}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="row g-3">
            <div className="col-12">
              <label className="small fw-bold text-muted text-uppercase">{t.labelPhoto}</label>
              <input type="file" className="form-control win10-input" onChange={(e) => setUserData({ ...userData, foto: e.target.files[0] })} />
            </div>
            <div className="col-12">
              <label className="small fw-bold text-muted text-uppercase">{t.labelName}</label>
              <input type="text" className="form-control win10-input" value={userData.nome || ""} onChange={(e) => setUserData({ ...userData, nome: e.target.value })} />
            </div>
            <div className="col-md-6">
              <label className="small fw-bold text-muted text-uppercase">{t.labelPhone}</label>
              <input type="text" className="form-control win10-input" value={userData.telefone || ""} onChange={(e) => setUserData({ ...userData, telefone: e.target.value })} />
            </div>
            <div className="col-md-6">
              <label className="small fw-bold text-muted text-uppercase">{t.labelLevel}</label>
              <select className="form-select win10-input" value={userData.nivel || "Caixa"} onChange={(e) => setUserData({ ...userData, nivel: e.target.value })}>
                <option value="Administrador">{t.levels[0]}</option>
                <option value="Caixa">{t.levels[1]}</option>
              </select>
            </div>
            <div className="col-12">
              <label className="small fw-bold text-muted text-uppercase">{t.labelEmail}</label>
              <input type="email" className="form-control win10-input" value={userData.email || ""} onChange={(e) => setUserData({ ...userData, email: e.target.value })} />
            </div>
            {!isEditing && (
              <div className="col-12">
                <label className="small fw-bold text-muted text-uppercase">{t.labelPass}</label>
                <input type="password" placeholder={t.passHint} className="form-control win10-input" onChange={(e) => setUserData({ ...userData, senha: e.target.value })} />
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light p-3">
          <button className="win10-btn win10-btn-outline px-4" onClick={() => setShowModal(false)}>{t.btnClose}</button>
          <button className="win10-btn win10-btn-primary px-4 fw-bold" onClick={handleSave}>{t.btnSave}</button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UsersTab;